import { useEffect } from "react";
import { useTranslation } from "react-i18next";

import { useSelectedLocation } from "components";
import { useRealTimeTypedQuery } from "components/WebSocket/hooks";
import { PIN_TYPES } from "models";
import { usePinChecklistsListeners } from "modules/PendingChecklistsDashboard/components/PinChecklists/listeners";
import { PendingChecklistsQueryKeys } from "modules/PendingChecklistsDashboard/queryKeys";
import { ApiInstance } from "utils/Api";

import type { TFunction } from "i18next";
import type { PinPendingChecklist } from "models";

export type PinPendingChecklistsByPinType = {
  pin_type: PIN_TYPES.Recall | PIN_TYPES.Remarks | PIN_TYPES.Warranty;
  checklists: PinPendingChecklist[];
  title: string;
};

const handleSelectPinPendingChecklists = (pinPendingChecklists: PinPendingChecklist[], t: TFunction) => {
  const groupedPendingChecklists: PinPendingChecklistsByPinType[] = [
    { pin_type: PIN_TYPES.Remarks, checklists: [], title: t("v8_remarks", "Remarks") },
    { pin_type: PIN_TYPES.Warranty, checklists: [], title: t("v8_warranty", "Warranty") },
    { pin_type: PIN_TYPES.Recall, checklists: [], title: t("v8_recall", "Recall") }
  ];

  pinPendingChecklists.forEach(ch => {
    const existingGroup = groupedPendingChecklists.find(group => group.pin_type === ch.pin_type);
    if (existingGroup) existingGroup.checklists.push(ch);
  });

  return groupedPendingChecklists.map(group => ({ ...group, checklists: group.checklists.sort((a, b) => a.ordering - b.ordering) }));
};

export const usePinPendingChecklistsQuery = () => {
  const { t } = useTranslation();
  const { selectedLocation } = useSelectedLocation();
  const listeners = usePinChecklistsListeners();

  const query = useRealTimeTypedQuery({
    queryKey: PendingChecklistsQueryKeys.pinPendingChecklists,
    queryFn: async ({ endpoint, baseUrl }) => (await ApiInstance.post(endpoint, { dealer_location_id: selectedLocation?.id }, baseUrl)).data.pin_pending_checklists,
    select: pinPendingChecklists => handleSelectPinPendingChecklists(pinPendingChecklists, t),
    enabled: false,
    listeners
  });

  useEffect(() => {
    if (selectedLocation?.id) query.refetch();
  }, [selectedLocation?.id]);

  return query;
};
